import { put, delay, takeEvery, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setHomePictreeCheckIn,
  setHomePictreePhotoShootNtt,
  setPictreePhotoShootByAssetNtt,
  patchHomePictreePhotoShootNtt,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import assetConfig from "../../utils/pictree/AssetConfig";
import chartConfig from "../../utils/ChartConfig";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000,   // 15s
  retries: 1,
};

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTreeNtt;
export const selectedSeasonNtt = (state) => state.pictreeNtt.selectedSeason;

function splitDateRangeByWeek(startDate, endDate) {
  const start = moment(startDate);
  const end = moment(endDate);
  const result = [];

  let current = start.clone();
  while (current.isBefore(end)) {
    const weekStart = current.clone();
    const weekEnd = moment.min(weekStart.clone().endOf("week"), end);
    result.push({
      weekStart: weekStart.format("YYYY-MM-DD"),
      weekEnd: weekEnd.format("YYYY-MM-DD"),
    });
    current = weekEnd.clone().add(1, "day");
  }
  return result;
}

// ===== クエリビルダ（承認イベント基準 / pad.type=NTT は動的）=====
const createApprovedBySubtypeQuery = ({ startUtc, endUtc, seasonId, needPadNtt }) => {
  // pad JOIN / 条件は Hokuden と共用シーズンだけ付与
  const padJoin  = needPadNtt ? `JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id` : ``;
  const padWhere = needPadNtt ? `AND pad.type = 'NTT_ME'` : ``;

  return {
    query : `
      SELECT pa.type, pa.sub_type, COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      ${padJoin}
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      WHERE rre.game_space_id = '${seasonId}'
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime <  '${endUtc}'
      ${padWhere}
      GROUP BY pa.type, pa.sub_type
      ORDER BY reviewOkcnt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

export function* getPictreeDataHomePhotoShootNtt2(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const seasonSelectedNtt = yield select(selectedSeasonNtt);
    if (!seasonSelectedNtt?.length) {
      // 初期化のみ
      yield put(setHomePictreePhotoShootNtt({
        countDenchu: 'loading',
        countUniqueUser: 'loading',
        countNewUser: undefined,
        countPic: 'loading',
        countAsset: 'loading',
        countDupeDenchu: 'loading',
        countReviewOk: 'loading',
        progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
        timings: {}, errors: {},
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        seasonSelectedNtt.length > 0 &&
        seasonSelectedNtt[0].seasonId !== undefined
      ) {
        const ids = seasonSelectedNtt
          .map((s) => `'${s.seasonId}'`)
          .join(", ");
        return seasonSelectedNtt.length === 1
          ? `AND gameSpaceId = ${ids}`         // ids は "'id1'"
          : `AND (gameSpaceId IN (${ids}))`;     // ids は "'id1','id2',…"
      }
      return "";
    })();

    const startUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // 初回ロード（全部loading）
    yield put(setHomePictreePhotoShootNtt({
      countDenchu: 'loading',
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countAsset: 'loading',
      countDupeDenchu: 'loading',
      countReviewOk: 'loading',
      progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
      timings: {}, errors: {},
    }));

    // max の事前設定（固定値）
    let maxNumber = 0;
    switch (seasonSelectedNtt[0].seasonId) {
      case "cmp4yz2lj00017rtcc4ok0jgv": maxNumber = 812206; break; // regular season1
      case "cmkdvml9l0001ngs48zepg7ne": maxNumber = 324649;  break; // tokyo, kanagawa, chiba, was 321650
      case "cmg7iw3vc00037bowekjy65pc": maxNumber = 15125;  break; // tochigi
      case "cmg7iw3va00017bow596s39cs": maxNumber = 240067; break; // nagano.yamanashi.sataima
      case "cmb68qvei00011vo0e591229z": maxNumber = 15461;  break; // hokkaido
      case "cmd5oyfft0001iho10o2iceha": maxNumber = 202163; break; // iwate
      case "cmadtlnih0001n3mx1wvl9zog": maxNumber = 150990; break; // aomori
      case "cm64mqm94000br3n0esu8c0n4": maxNumber = 150099; break; // kita-kanto
      case "cm46zidp30003bpp6cxq101mo": maxNumber = 2957;   break; // akita
      default: break;
    }

    const needPad = (seasonSelectedNtt[0].seasonId === "cmb68qvei00011vo0e591229z");
    const padJoin  = needPad ? `JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id` : ``;
    const padWhere = needPad ? `AND pad.type = 'NTT_ME'` : ``;

    const sqlQueryCountPhotoDenchu = {
      "query": `
        SELECT COUNT(DISTINCT pae.power_asset_id) AS total_photo_power_assets
        FROM power_asset_event pae
        ${padJoin}
        WHERE pae.game_space_id = '${seasonSelectedNtt[0].seasonId}'
          AND pae.type = 'PHOTOGRAPH'
          ${padWhere}
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime <  '${endUtc}';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    const padJoinRR  = needPad ? `JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id` : ``;
    const padWhereRR = needPad ? `AND pad.type = 'NTT_ME'` : ``;
    const sqlQueryCountUniqueUser = {
      "query": `
        SELECT COUNT(DISTINCT rr.player_id) AS unique_photographers
        FROM review_request_event rre
        JOIN review_request rr ON rr.id = rre.review_request_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        ${padJoinRR}
        WHERE rr.game_space_id = '${seasonSelectedNtt[0].seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        ${padWhereRR};
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountPicCount = {
      "query": `
        SELECT
          SUM(picCount) AS total_pic_count
        FROM (
          SELECT picCount FROM DenchuPhotoShootDenchuEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo')
          BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59'
          AND property IN ('ntt', 'nttme') ${seasonQuery}
        ) AS combined_data;`,
      "database": "KpiDashboard",
      "type": "list"
    };

    // dupe photoshoot - denchu
    const padJoinPad  = needPad ? `JOIN power_asset_detail AS pad ON pad.power_asset_id = pae.power_asset_id` : ``;
    const padWherePad = needPad ? `AND pad.type = 'NTT_ME'` : ``;
    const sqlQueryCountPhotoDupeDenchu = {
      "query": `
        SELECT COUNT(*) AS dupe_asset_count
        FROM (
          SELECT pae.power_asset_id
          FROM power_asset_event AS pae
          ${padJoinPad}
          WHERE pae.type = 'PHOTOGRAPH'
            ${padWherePad}
            AND pae.game_space_id = '${seasonSelectedNtt[0].seasonId}'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
          GROUP BY pae.power_asset_id
          HAVING COUNT(*) > 1
        ) AS t;
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    const sqlQueryCountNewUsers = {
      query: `
        SELECT COUNT(*) AS first_time_photographers
        FROM (
          SELECT DISTINCT rr.player_id
          FROM review_request_event rre
          JOIN review_request rr ON rr.id = rre.review_request_id
          JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
          JOIN review_request_photo_bind bind ON bind.review_request_id = rr.id
          WHERE rr.game_space_id = '${seasonSelectedNtt[0].seasonId}'
            AND rre.type = 'APPROVED'
            AND rre.occurred_datetime BETWEEN
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
              AND CONVERT_TZ('${rangeTarget[1]} 23:59:59','Asia/Tokyo','UTC')
        ) AS c
        WHERE NOT EXISTS (
          SELECT 1
          FROM review_request_event pre
          JOIN review_request rr_pre ON rr_pre.id = pre.review_request_id
          WHERE rr_pre.player_id = c.player_id
            AND pre.type = 'APPROVED'
            AND pre.occurred_datetime <
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
        );
      `,
      database: 'KpiDashboard',
      type: 'list'
    };

    const sqlQueryCountReviewOk = {
      "query": `
        SELECT COUNT(DISTINCT rr.power_asset_id) AS reviewOk_count
        FROM review_request_event rre
        JOIN review_request rr ON rre.review_request_id = rr.id
        ${padJoinRR}
        WHERE rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        AND rre.game_space_id = '${seasonSelectedNtt[0].seasonId}'
        ${padWhereRR};
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    // ========= 1) 優先バッチ =========
    const [
      dataCountReviewOk
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryCountReviewOk, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    // 先に描画
    const countReviewOk = dataCountReviewOk[0]["reviewOk_count"];

    yield put(setHomePictreePhotoShootNtt({
      countDenchu: 'loading',
      countAsset : 'loading',
      countReviewOk,
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countDupeDenchu: 'loading',
      progressDenchu: { taken: countReviewOk, max: maxNumber, maxByasset: maxNumber },
      timings: {}, errors: {},
    }));

    yield put(patchHomePictreePhotoShootNtt({
      //countPic: countPicCountValue == null ? 0 : parseInt(countPicCountValue, 10),
      progressDenchu: { taken: countReviewOk, max: maxNumber, maxByasset: maxNumber },
    }));

    // ========= 2) 次バッチ（並列2本） =========
    const [
      dataCountUniue,
      dataPhotoDenchu,
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountUniqueUser, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);
    const countPhotoDenchuValue = dataPhotoDenchu[0]["total_photo_power_assets"];
    const countUniqueUserValue = dataCountUniue[0]["unique_photographers"];
    yield put(patchHomePictreePhotoShootNtt({
      countUniqueUser: countUniqueUserValue,
      countDenchu: countPhotoDenchuValue,
      countAsset: countPhotoDenchuValue,
    }));

    const dataPicCountResp = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPicCount, ENDPOINT.GET_DATA_USERS, jwtToken],
      USERS_LIGHT_RETRY
    );
    const picStr = JSON.parse(dataPicCountResp.body)?.[0]?.[0]?.stringValue;
    const countPicNum = picStr == null ? 0 : parseInt(picStr, 10, 10);
    yield put(patchHomePictreePhotoShootNtt({ countPic: countPicNum }));

    // ========= 3) 最後のバッチ（重複：1本だけ）=========
    const dataDupePhotoDenchu = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPhotoDupeDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const countDupeDenchuCountValue = dataDupePhotoDenchu[0]["dupe_asset_count"];
    yield put(patchHomePictreePhotoShootNtt({ countDupeDenchu: countDupeDenchuCountValue }));

    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataHomeCheckInNttFromSxi(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeasonNtt);

    if (!rangeTarget || rangeTarget.length !== 2) {
      console.error("Invalid rangeTarget");
      yield put(setPageTopLoader(false));
      return;
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }
    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", ");

    const sqlQueryCheckinSummary = {
      query: `
        SELECT COUNT(*) AS COUNT,
        COUNT(DISTINCT pae.power_asset_id) AS ASSET_COUNT,
        COUNT(DISTINCT pae.player_id) AS PLAYER_COUNT,
        MAX(ps.name) AS MOST_CHECKIN,
        MAX(pae.power_asset_id) AS MOST_ASSET
        FROM power_asset_event pae
        JOIN player_state ps ON pae.player_id = ps.player_id
        JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
        WHERE CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59' AND pae.type in ("CHECK_IN")
        AND pae.game_space_id IN (${seasonIdList})
        AND pad.type = 'NTT_ME'
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [summaryTemp] = yield all([
      call(
        getDataFromSxi,
        sqlQueryCheckinSummary,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
    ]);

    const homeCheckInData = {
        checkInCount: summaryTemp[0]["COUNT"],
        checkInCountKisu: summaryTemp[0]["ASSET_COUNT"],
        checkInCountUniqueUser: summaryTemp[0]["PLAYER_COUNT"],
        checkInMost: summaryTemp[0]["MOST_ASSET"],
        checkInMostUser: summaryTemp[0]["MOST_CHECKIN"],
        checkInRatio: "--", //checkInRatio,
      };
    yield put(setHomePictreeCheckIn(homeCheckInData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataPhotoShootByAssetNtt2(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const seasonSelectedNtt = yield select(selectedSeasonNtt);
    const seasonId = seasonSelectedNtt?.[0]?.seasonId || null;
    const needPadNtt = (seasonId === "cmb68qvei00011vo0e591229z");

    const startUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // プレースホルダ
    yield put(setPictreePhotoShootByAssetNtt([{ subType: "loading", CNT: 0, label: "-" }]));

    const singleSql = createApprovedBySubtypeQuery({ startUtc, endUtc, seasonId, needPadNtt });

    const sxiEffects = [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [singleSql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ];

    const [
      rows,
    ] = yield call(runEffectsInBatches, sxiEffects, { concurrency: 2, gapMs: 300 });

    // ---- 整形：type優先順 + 同type内は通常先 + 件数降順 ----
    const labelMap = chartConfig.chartLabelPicTreeAssetTypeNtt || assetConfig.assetSubTypeNtt;
    const typeRank = { POWER_POLE: 1, MAN_HOLE: 2, HAND_HOLE: 3 };
    const isLucky = (subType) => (subType || "").includes("LUCKY");
    const getLabel = (type, subType) => {
      if (type === "MAN_HOLE" && subType === "NORMAL") return "マンホール";
      if (type === "HAND_HOLE" && subType === "NORMAL") return "ハンドホール";
      return labelMap[subType] || subType || `${type}`;
    };

    const payload = (rows || [])
      .map(({ type, sub_type, reviewOkcnt }) => ({
        type: type || "UNKNOWN",
        subType: sub_type,
        CNT: Number(reviewOkcnt) || 0,
        label: getLabel(type, sub_type),
      }))
      .filter((item) => item.CNT > 0)
      .sort((a, b) => {
        const rankDiff = (typeRank[a.type] || 99) - (typeRank[b.type] || 99);
        if (rankDiff !== 0) return rankDiff;
        const luckyDiff = Number(isLucky(a.subType)) - Number(isLucky(b.subType));
        if (luckyDiff !== 0) return luckyDiff;
        return b.CNT - a.CNT;
      })
      .map(({ type, subType, CNT, label }) => ({ type, subType, CNT, label }));
    yield put(setPictreePhotoShootByAssetNtt(payload));
    } catch (err) {
      console.log("[getPictreeDataPhotoShootByAssetNtt2] ERROR", err);
    } finally {
      yield put(setPageTopLoader(false));
    }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_HOME_PHOTOSHOOT_NTT, getPictreeDataHomePhotoShootNtt2);
  yield takeEvery(PICTREE.GET_HOME_CHECKIN_NTT, getPictreeDataHomeCheckInNttFromSxi);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_BY_ASSET_NTT, getPictreeDataPhotoShootByAssetNtt2);
}
